/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Card — macOS 风格毛玻璃卡片容器
 *
 * - 16px 大圆角（--radius-card）
 * - 双层柔和阴影（--shadow-card / --shadow-card-hover）
 * - 毛玻璃效果（半透明背景 + backdrop-blur）
 * - 三个尺寸：default / sm / lg
 */
function Card({
  className,
  size = 'default',
  ...props
}: React.ComponentProps<'div'> & { size?: 'default' | 'sm' | 'lg' }) {
  return (
    <div
      data-slot='card'
      data-size={size}
      className={cn(
        'group/card bg-[var(--bg-glass)] text-card-foreground ring-foreground/5 flex flex-col gap-4 overflow-hidden rounded-[var(--radius-card)] py-4 text-sm ring-1 shadow-[var(--shadow-card)] backdrop-blur-[20px] saturate-[180%] transition-all duration-250',
        'hover:shadow-[var(--shadow-card-hover)]',
        'has-data-[slot=card-footer]:pb-0 has-[>img:first-child]:pt-0',
        'data-[size=sm]:gap-3 data-[size=sm]:py-3 data-[size=sm]:has-data-[slot=card-footer]:pb-0',
        'data-[size=lg]:gap-6 data-[size=lg]:py-6',
        '*:[img:first-child]:rounded-t-[var(--radius-card)] *:[img:last-child]:rounded-b-[var(--radius-card)]',
        '[transition-timing-function:var(--ease-out-expo)]',
        className
      )}
      {...props}
    />
  )
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-header'
      className={cn(
        'group/card-header @container/card-header grid auto-rows-min items-start gap-1 rounded-t-[var(--radius-card)] px-4',
        'group-data-[size=sm]/card:px-3 group-data-[size=lg]/card:px-6',
        'has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto]',
        '[.border-b]:pb-4 group-data-[size=sm]/card:[.border-b]:pb-3 group-data-[size=lg]/card:[.border-b]:pb-6',
        className
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-title'
      className={cn(
        'text-base leading-snug font-medium tracking-tight',
        'group-data-[size=sm]/card:text-sm group-data-[size=lg]/card:text-lg',
        className
      )}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-description'
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-action'
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-content'
      className={cn(
        'px-4 group-data-[size=sm]/card:px-3 group-data-[size=lg]/card:px-6',
        className
      )}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='card-footer'
      className={cn(
        'bg-muted/50 flex items-center rounded-b-[var(--radius-card)] border-t p-4',
        'group-data-[size=sm]/card:p-3 group-data-[size=lg]/card:p-6',
        className
      )}
      {...props}
    />
  )
}

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
}